'use client';

import { Badge } from '@/components/ui/badge';
import type { IAgent } from '@/lib/types';
import { Building, Calendar, Award, Star, Quote, Briefcase, GraduationCap } from 'lucide-react';
import Image from 'next/image';
import { motion } from 'framer-motion';

interface AgentDetailProps {
  agent: IAgent;
  isModal?: boolean;
}

export function AgentDetail({ agent, isModal = false }: AgentDetailProps) {
  return (
    <div className={`w-full overflow-hidden ${isModal ? "" : "container mx-auto px-4 py-8"}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        {/* Premium Hero Header Section */}
        <div className="relative h-48 md:h-64 mb-16">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/80 to-blue-600 rounded-t-2xl opacity-90" />
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 rounded-t-2xl" />

          {/* Profile Image & Basic Info Wrapper */}
          <div className="absolute -bottom-12 left-8 right-8 flex flex-col md:flex-row items-end gap-6">
            <div className="relative group">
              <div className="absolute inset-0 bg-white/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-300" />
              <Image
                src={agent.profilePhotoUrl || '/images/placeholder-agent.jpg'}
                alt={agent.name}
                width={160}
                height={160}
                className="relative rounded-full border-8 border-background shadow-2xl object-cover aspect-square"
              />
            </div>
            <div className="mb-4 text-center md:text-left">
              <h1 className="text-3xl md:text-4xl font-extrabold text-white drop-shadow-xl">
                {agent.name}
              </h1>
              <p className="text-white/90 font-semibold text-lg flex items-center justify-center md:justify-start gap-2 drop-shadow-md">
                {agent.title}
              </p>
            </div>
          </div>
        </div>

        <div className="px-8 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-10 mt-12">
          {/* Left Column - Stats & Quick Info */}
          <div className="lg:col-span-4 space-y-8">
            <div className="bg-muted/30 rounded-3xl p-8 border border-muted backdrop-blur-sm">
              <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-6">Performance</h3>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary">
                    <Building className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-xl font-bold">{agent.numberOfProperties}</div>
                    <div className="text-sm text-muted-foreground whitespace-nowrap">Properties Managed</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-500/10 rounded-2xl flex items-center justify-center text-blue-600">
                    <Award className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-xl font-bold">100%</div>
                    <div className="text-sm text-muted-foreground">Client Satisfaction</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-500/10 rounded-2xl flex items-center justify-center text-orange-600">
                    <Calendar className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-xl font-bold">{agent.createdAt ? new Date(agent.createdAt).getFullYear() : '2024'}</div>
                    <div className="text-sm text-muted-foreground">Joined Our Team</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-primary/5 rounded-3xl p-8 border border-primary/10">
              <div className="flex items-center gap-3 mb-4 text-primary">
                <Quote className="w-8 h-8 opacity-20" />
              </div>
              <p className="italic text-muted-foreground leading-relaxed">
                "Excellence is not a skill, it's an attitude. I'm here to ensure your journey home is seamless and rewarding."
              </p>
            </div>
          </div>

          {/* Right Column - Narrative & Expertise */}
          <div className="lg:col-span-8 space-y-12">
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
                  <Briefcase className="w-5 h-5" />
                </div>
                <h2 className="text-2xl font-bold">Biography</h2>
              </div>
              <p className="text-xl text-foreground font-medium mb-4 leading-relaxed tracking-tight">
                {agent.name} is a cornerstone of our real estate division, bringing a wealth of local market intelligence and a client-first philosophy.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                With a track record of handling high-value transactions and a deep understanding of urban development, they consistently deliver results that exceed expectations. Specializing in {agent.title}, they handle each listing with surgical precision, ensuring that both property value and client needs are aligned for the best possible outcome.
              </p>
            </section>

            <div className="h-px bg-gradient-to-r from-muted via-muted/50 to-transparent" />

            <section>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
                  <Star className="w-5 h-5" />
                </div>
                <h2 className="text-2xl font-bold">Expertise & Strategy</h2>
              </div>
              <div className="flex flex-wrap gap-4">
                {['Residential Strategy', 'Luxury Portfolio', 'Investment Analysis', 'Market Valuation', 'Property Management', 'Urban Zoning'].map((tag) => (
                  <div
                    key={tag}
                    className="group relative px-6 py-3 rounded-2xl bg-secondary/50 border border-muted hover:border-primary/50 hover:bg-primary/5 transition-all duration-300 cursor-default"
                  >
                    <span className="relative z-10 text-sm font-semibold text-muted-foreground group-hover:text-primary transition-colors">{tag}</span>
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl" />
                  </div>
                ))}
              </div>
            </section>

            <section className="bg-muted/20 rounded-3xl p-8 border border-dashed">
              <div className="flex items-center gap-3 mb-4">
                <GraduationCap className="w-6 h-6 text-muted-foreground" />
                <h2 className="text-xl font-bold">Experience & Qualifications</h2>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                  Certified Residential Specialist (CRS)
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                  B.S. in Real Estate Management
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                  Top Producer Award 2024-2025
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                  Expert in Urban Zoning & Compliance
                </li>
              </ul>
            </section>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
